import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaTimes, FaUserPlus } from 'react-icons/fa';

const CollaboratorSelect = ({ value = [], onChange, tokenConfig }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Search users with debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!search.trim()) {
      setResults([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/users/collaborators?search=${encodeURIComponent(search)}`, tokenConfig);
        // Filter out already-selected users
        const selectedIds = value.map(u => u._id);
        setResults(res.data.filter(u => !selectedIds.includes(u._id)));
      } catch (err) {
        setResults([]);
      }
      setLoading(false);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, value, tokenConfig]);

  const addCollaborator = (user) => {
    onChange([...value, user]);
    setSearch('');
    setResults([]);
    setIsOpen(false);
  };

  const removeCollaborator = (userId) => {
    onChange(value.filter(u => u._id !== userId));
  };

  return (
    <div ref={wrapperRef} className="relative mb-4">
      <label className="block text-sm font-medium text-[#054581] mb-2">
        <FaUserPlus className="inline w-4 h-4 mr-1" />
        Add Collaborators (Optional)
      </label>

      {/* Selected collaborators */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {value.map(user => (
            <span
              key={user._id}
              className="inline-flex items-center gap-1 px-3 py-1 bg-[#1086a4]/10 text-[#054581] rounded-full text-sm font-medium"
            >
              {user.name} {user.lastName}
              <button
                type="button"
                onClick={() => removeCollaborator(user._id)}
                className="ml-1 text-[#054581] hover:text-red-600 transition-colors"
              >
                <FaTimes className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <input
        type="text"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => search.trim() && setIsOpen(true)}
        placeholder="Search by name or email..."
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1086a4] focus:border-transparent text-sm"
      />

      {/* Dropdown results */}
      {isOpen && (search.trim()) && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-500">Searching...</div>
          ) : results.length > 0 ? (
            results.map(user => (
              <button
                key={user._id}
                type="button"
                onClick={() => addCollaborator(user)}
                className="w-full px-4 py-2 text-left hover:bg-[#1086a4]/10 transition-colors flex items-center justify-between"
              >
                <span className="text-sm text-gray-800">{user.name} {user.lastName}</span>
                <span className="text-xs text-gray-500">{user.email}</span>
              </button>
            ))
          ) : (
            <div className="px-4 py-3 text-sm text-gray-500">No users found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default CollaboratorSelect;
